/**
 * Shared invoice financial semantics (Phase 2A).
 *
 * Single source of truth for "does this invoice represent money currently
 * owed?" so the Client Profile summary stays consistent with the backend AR
 * definition and never drifts. Reuses existing invoice status semantics — it
 * does NOT introduce a new ledger.
 *
 * CANONICAL SEMANTICS (verified against backend, June 2026)
 * ─────────────────────────────────────────────────────────
 * The backend Dashboard AR metric (routes/dashboard_routes.py) defines
 * outstanding receivables as invoices whose `status` is in {sent, draft},
 * and separately surfaces failed states (`failed_checkout`, `autoChargeFailed`)
 * as "attention needed" — those are still money owed (nothing was collected).
 *
 * We therefore use an EXPLICIT ALLOW-LIST of statuses that represent money
 * currently owed. This is future-safe: an unknown / newly-introduced invoice
 * status will NOT silently inflate outstanding AR unless it is deliberately
 * added here.
 *
 *   OWED (counts toward outstanding balance & open count):
 *     - 'sent'            invoice issued, payment link out, not yet paid
 *     - 'draft'           invoice created, not yet finalized (matches dashboard)
 *     - 'failed_checkout' auto-charge / checkout failed → still owed, collectible
 *
 *   NOT OWED (excluded):
 *     - 'paid'                fully collected
 *     - 'refunded'            reversed
 *     - 'partially_refunded'  was PAID then partly reversed. It is NOT a new
 *                             receivable — e.g. a $120 invoice paid then $40
 *                             refunded must show $0 outstanding, not $120.
 *     - 'pending' / 'pending_intent'  charge in-flight (PaymentIntent being
 *                             confirmed) — not a collectible receivable, and
 *                             excluded by the backend AR calc too.
 *     - 'void' / 'canceled' / 'cancelled'  defensive; never money owed.
 */

// Explicit set of invoice statuses that represent money currently owed.
const OUTSTANDING_STATUSES = new Set([
  'sent',
  'draft',
  'failed_checkout',
]);

// Canonical owed statuses as an array (for Firestore `in` queries). Single
// source of truth shared with the company Invoices module.
export const OWED_STATUSES = [...OUTSTANDING_STATUSES];

/**
 * True when an invoice still represents money owed (collectible).
 * Uses an explicit allow-list — unknown statuses are treated as NOT owed.
 */
export function isOutstanding(inv) {
  if (!inv) return false;
  const status = (inv.status || '').toLowerCase();
  return OUTSTANDING_STATUSES.has(status);
}

/**
 * Outstanding dollar amount for a single invoice.
 * OWED invoices (sent/draft/failed_checkout) have collected nothing, so the
 * full `amount` is outstanding. Non-owed invoices contribute $0.
 */
export function outstandingAmount(inv) {
  if (!isOutstanding(inv)) return 0;
  return parseFloat(inv?.amount) || 0;
}

/** Sum of outstanding invoice amounts (dollars). */
export function sumOutstanding(invoices = []) {
  return invoices.reduce((s, inv) => s + outstandingAmount(inv), 0);
}

/** Count of open (outstanding) invoices. */
export function countOpen(invoices = []) {
  return invoices.filter(isOutstanding).length;
}

/** The subset of outstanding invoices. */
export function openInvoices(invoices = []) {
  return invoices.filter(isOutstanding);
}

/** Format a dollar amount consistently. */
export function formatMoney(amt) {
  return `$${(parseFloat(amt) || 0).toFixed(2)}`;
}

/** Whether a client has an actively chargeable card on file. */
export function hasActiveCard(client) {
  return (client?.cardOnFileStatus || '') === 'active';
}

/**
 * Decide the collection action for a Payment History row.
 *
 * Centralizes the rule so the Client Profile never drifts from the backend's
 * collection semantics:
 *   - 'retry'  → failed AutoPay (autoChargeFailed & unpaid) uses the
 *                specialized recovery path (POST /api/payments/charge-invoice).
 *   - 'charge' → any other unpaid, non-prepay invoice uses the UNIVERSAL
 *                Charge Now flow (ChargeNowModal → POST /api/payments/charge-now).
 *   - 'none'   → paid invoices and prepay packages are not collectible here.
 *
 * @returns {'retry' | 'charge' | 'none'}
 */
export function resolveRowAction(inv) {
  if (!inv) return 'none';
  const status = (inv.status || '').toLowerCase();
  if (status === 'paid') return 'none';
  if (inv.autoChargeFailed) return 'retry';
  if ((inv.invoiceType || '').toLowerCase() === 'prepay') return 'none';
  return 'charge';
}



const round2 = (n) => Math.round(((parseFloat(n) || 0) + Number.EPSILON) * 100) / 100;

/**
 * Phase 2B — DERIVE a job's financial state from its matched invoice records.
 *
 * Invoices remain the single source of truth; NOTHING is written to the job.
 * The caller aggregates all invoices linked to the job (primarily by
 * `invoice.taskId === task.id`; deposit + final + any task-linked artifacts)
 * and passes them here. Prepay-covered visits carry the existing, backend-set
 * operational flag `task.isCoveredByPackage` (NOT a financial field) — used
 * only as a no-invoice fallback so covered visits don't read as "Unbilled".
 *
 * Reuses canonical invoice semantics (isOutstanding / outstandingAmount) so it
 * can never drift from the AR definition. Deterministic + unit-testable.
 *
 * @returns {{
 *   state:'paid'|'outstanding'|'payment_failed'|'refunded'|'partially_refunded'|'covered'|'unbilled',
 *   label:string, amountBilled:number, amountOutstanding:number,
 *   amountPaid:number, refundedAmount:number, invoiceCount:number, attention:boolean
 * }}
 */
export function deriveJobFinancialState({ task = {}, invoices = [] } = {}) {
  const list = Array.isArray(invoices) ? invoices : [];
  const invoiceCount = list.length;
  const base = { amountBilled: 0, amountOutstanding: 0, amountPaid: 0, refundedAmount: 0, invoiceCount };

  if (invoiceCount === 0) {
    // Explicit billing intent takes priority over the operational
    // package-coverage flag. A MISSING billingIntent is legacy/unknown and is
    // never interpreted as billable at read time.
    const intent = (task?.billingIntent || '').toLowerCase();
    if (intent === 'non_billable') {
      return {
        ...base,
        state: 'no_charge',
        label: 'No Charge',
        attention: false,
        nonBillableReason: task?.nonBillableReason || null,
      };
    }
    if (task && task.isCoveredByPackage) {
      return { ...base, state: 'covered', label: 'Covered by Package', attention: false };
    }
    // Explicitly billable but completed with NO financial artifact → the
    // operator still owes a billing action. Legacy no-intent jobs stay Unbilled.
    if (intent === 'billable' && (task?.status || '') === 'completed') {
      return { ...base, state: 'billing_attention', label: 'Billing Attention', attention: true };
    }
    return { ...base, state: 'unbilled', label: 'Unbilled', attention: false };
  }

  let amountBilled = 0, amountOutstanding = 0, amountPaid = 0, refundedAmount = 0;
  let hasAttention = false, hasFullRefund = false, hasPartialRefund = false;

  for (const inv of list) {
    const status = (inv.status || '').toLowerCase();
    const amt = parseFloat(inv.amount) || 0;
    amountBilled += amt;
    amountOutstanding += outstandingAmount(inv);
    if (status === 'paid') amountPaid += amt;
    if (status === 'partially_refunded') { amountPaid += amt; hasPartialRefund = true; }
    if (status === 'refunded') hasFullRefund = true;
    refundedAmount += parseFloat(inv.refundedAmount) || 0;
    if ((inv.autoChargeFailed && status !== 'paid') || status === 'failed_checkout') hasAttention = true;
  }

  amountBilled = round2(amountBilled);
  amountOutstanding = round2(amountOutstanding);
  amountPaid = round2(amountPaid);
  refundedAmount = round2(refundedAmount);
  const agg = { amountBilled, amountOutstanding, amountPaid, refundedAmount, invoiceCount };

  // Priority: attention (failed) > net outstanding > refund states > paid.
  if (hasAttention && amountOutstanding > 0) {
    return { ...agg, state: 'payment_failed', label: `Payment Failed · ${formatMoney(amountOutstanding)}`, attention: true };
  }
  if (amountOutstanding > 0) {
    return { ...agg, state: 'outstanding', label: `${formatMoney(amountOutstanding)} Outstanding`, attention: false };
  }
  if (hasPartialRefund) {
    return { ...agg, state: 'partially_refunded', label: 'Partially Refunded', attention: false };
  }
  if (hasFullRefund) {
    return { ...agg, state: 'refunded', label: 'Refunded', attention: false };
  }
  return { ...agg, state: 'paid', label: amountPaid > 0 ? `Paid ${formatMoney(amountPaid)}` : 'Paid', attention: false };
}

/** Tailwind classes for a job financial badge, keyed by derived state. */
export function jobFinancialBadgeClass(state) {
  switch (state) {
    case 'paid':               return 'bg-green-100 text-green-700';
    case 'outstanding':        return 'bg-amber-100 text-amber-800';
    case 'payment_failed':     return 'bg-red-100 text-red-700';
    case 'refunded':           return 'bg-gray-100 text-gray-600';
    case 'partially_refunded': return 'bg-gray-100 text-gray-600';
    case 'covered':            return 'bg-blue-100 text-blue-700';
    case 'no_charge':          return 'bg-slate-100 text-slate-600';
    case 'billing_attention':  return 'bg-orange-100 text-orange-700';
    case 'unbilled':           return 'bg-gray-100 text-gray-500';
    default:                   return 'bg-gray-100 text-gray-500';
  }
}
